import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from 'antd'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import Dashboard from './pages/Dashboard'
import Requests from './pages/Requests'
import RequestDetails from './pages/RequestDetails'
import Review from './pages/Review'
import Analytics from './pages/Analytics'
import ModelManagement from './pages/ModelManagement'
import ApiOperations from './pages/ApiOperations'
import OcrAnalysis from './pages/OcrAnalysis'
import Login from './pages/Login'

const { Content } = Layout

const isAuthenticated = () => !!localStorage.getItem('kredit_api_key')

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isAuthenticated()) return <Navigate to="/login" replace />
  return <>{children}</>
}

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const lastMobileState = useRef<boolean | null>(null)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992
      setIsMobile(mobile)

      // Only force sidebar state when crossing breakpoint boundaries.
      if (lastMobileState.current !== mobile) {
        setSidebarCollapsed(mobile)
        lastMobileState.current = mobile
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarCollapsed(true)
    }
  }

  // Calculate margin for main content
  const getMainMarginLeft = () => {
    if (isMobile) return '0' // No margin on mobile (overlay)
    if (sidebarCollapsed) return '80px' // Collapsed desktop sidebar
    return '250px' // Full desktop sidebar
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout style={{ minHeight: '100vh' }}>
              <Sidebar
                collapsed={sidebarCollapsed}
                isMobile={isMobile}
                onClose={closeSidebar}
              />
              <Layout style={{
                marginLeft: getMainMarginLeft(),
                transition: 'margin-left 0.3s ease'
              }}>
                <Header
                  isMobile={isMobile}
                  sidebarCollapsed={sidebarCollapsed}
                  onToggleSidebar={toggleSidebar}
                />
                <Content style={{
                  margin: isMobile ? '12px' : '16px',
                  background: '#f5f7fb',
                  padding: isMobile ? '12px' : '20px',
                  borderRadius: isMobile ? '12px' : '16px',
                  minHeight: isMobile ? 'calc(100vh - 88px)' : 'calc(100vh - 96px)'
                }}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/requests" element={<Requests />} />
                    <Route path="/requests/:id" element={<RequestDetails />} />
                    <Route path="/review" element={<Review />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/model" element={<ModelManagement />} />
                    <Route path="/api-operations" element={<ApiOperations />} />
                    <Route path="/ocr" element={<OcrAnalysis />} />
                  </Routes>
                </Content>
              </Layout>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default App